import React, { useRef, useEffect } from 'react';
import { styled } from 'styled-components';
import { gsap } from 'gsap';

const FloatingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: -25;
  perspective: 1000px;
`;

const FloatingElement = styled.div<{ size: number; delay: number }>`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  transform-style: preserve-3d;
  animation: float ${props => 8 + props.delay}s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
  
  @keyframes float {
    0%, 100% { 
      transform: translateY(0px) rotateX(0deg) rotateY(0deg) rotateZ(0deg);
    }
    25% { 
      transform: translateY(-20px) rotateX(90deg) rotateY(45deg) rotateZ(15deg);
    }
    50% { 
      transform: translateY(-10px) rotateX(180deg) rotateY(90deg) rotateZ(30deg);
    }
    75% { 
      transform: translateY(-15px) rotateX(270deg) rotateY(135deg) rotateZ(45deg);
    }
  }
`;

const Cube = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  
  .face {
    position: absolute;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(68, 255, 153, 0.05));
    border: 1px solid rgba(0, 255, 136, 0.3);
    backdrop-filter: blur(10px);
  }
  
  .front { transform: rotateY(0deg) translateZ(50%); }
  .back { transform: rotateY(180deg) translateZ(50%); }
  .right { transform: rotateY(90deg) translateZ(50%); }
  .left { transform: rotateY(-90deg) translateZ(50%); }
  .top { transform: rotateX(90deg) translateZ(50%); }
  .bottom { transform: rotateX(-90deg) translateZ(50%); }
`;

const Pyramid = styled.div`
  width: 0;
  height: 0;
  position: relative;
  transform-style: preserve-3d;
  
  .pyramid-face {
    position: absolute;
    background: linear-gradient(135deg, rgba(0, 255, 136, 0.08), rgba(68, 255, 153, 0.03));
    border: 1px solid rgba(0, 255, 136, 0.2);
    backdrop-filter: blur(5px);
  }
  
  .base {
    width: 80px;
    height: 80px;
    transform: rotateX(-90deg) translateZ(-40px);
    left: -40px;
    top: -40px;
  }
  
  .face1, .face2, .face3, .face4 {
    width: 0;
    height: 0;
    border-left: 40px solid transparent;
    border-right: 40px solid transparent;
    border-bottom: 60px solid rgba(0, 255, 136, 0.1);
  }
  
  .face1 { transform: rotateY(0deg) translateZ(28px) translateY(-30px); }
  .face2 { transform: rotateY(90deg) translateZ(28px) translateY(-30px); }
  .face3 { transform: rotateY(180deg) translateZ(28px) translateY(-30px); }
  .face4 { transform: rotateY(270deg) translateZ(28px) translateY(-30px); }
`;

const Octahedron = styled.div`
  width: 0;
  height: 0;
  position: relative;
  transform-style: preserve-3d;
  
  .octa-face {
    position: absolute;
    width: 0;
    height: 0;
    border-left: 25px solid transparent;
    border-right: 25px solid transparent;
    border-bottom: 25px solid rgba(0, 255, 136, 0.06);
  }
`;

const Floating3DElements: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Create floating elements
    const elements = [
      { type: 'cube', count: 8 },
      { type: 'pyramid', count: 6 },
      { type: 'octahedron', count: 10 }
    ];

    elements.forEach(({ type, count }) => {
      for (let i = 0; i < count; i++) {
        const element = document.createElement('div');
        element.className = 'floating-element';
        
        const size = Math.random() * 40 + 20;
        const x = Math.random() * window.innerWidth;
        const y = Math.random() * window.innerHeight;
        const delay = Math.random() * 5;
        
        element.style.cssText = `
          position: absolute;
          left: ${x}px;
          top: ${y}px;
          width: ${size}px;
          height: ${size}px;
          transform-style: preserve-3d;
          animation: float ${8 + delay}s ease-in-out infinite;
          animation-delay: ${delay}s;
        `;

        // Create the 3D shape based on type
        if (type === 'cube') {
          element.innerHTML = `
            <div style="width: 100%; height: 100%; position: relative; transform-style: preserve-3d;">
              <div class="face front" style="position: absolute; width: 100%; height: 100%; background: linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(68, 255, 153, 0.05)); border: 1px solid rgba(0, 255, 136, 0.3); backdrop-filter: blur(10px); transform: rotateY(0deg) translateZ(${size/2}px);"></div>
              <div class="face back" style="position: absolute; width: 100%; height: 100%; background: linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(68, 255, 153, 0.05)); border: 1px solid rgba(0, 255, 136, 0.3); backdrop-filter: blur(10px); transform: rotateY(180deg) translateZ(${size/2}px);"></div>
              <div class="face right" style="position: absolute; width: 100%; height: 100%; background: linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(68, 255, 153, 0.05)); border: 1px solid rgba(0, 255, 136, 0.3); backdrop-filter: blur(10px); transform: rotateY(90deg) translateZ(${size/2}px);"></div>
              <div class="face left" style="position: absolute; width: 100%; height: 100%; background: linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(68, 255, 153, 0.05)); border: 1px solid rgba(0, 255, 136, 0.3); backdrop-filter: blur(10px); transform: rotateY(-90deg) translateZ(${size/2}px);"></div>
              <div class="face top" style="position: absolute; width: 100%; height: 100%; background: linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(68, 255, 153, 0.05)); border: 1px solid rgba(0, 255, 136, 0.3); backdrop-filter: blur(10px); transform: rotateX(90deg) translateZ(${size/2}px);"></div>
              <div class="face bottom" style="position: absolute; width: 100%; height: 100%; background: linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(68, 255, 153, 0.05)); border: 1px solid rgba(0, 255, 136, 0.3); backdrop-filter: blur(10px); transform: rotateX(-90deg) translateZ(${size/2}px);"></div>
            </div>
          `;
        }

        container.appendChild(element);

        // Add mouse interaction
        const handleMouseMove = (e: MouseEvent) => {
          const rect = element.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          const deltaX = (e.clientX - centerX) * 0.1;
          const deltaY = (e.clientY - centerY) * 0.1;
          
          gsap.to(element, {
            x: deltaX,
            y: deltaY,
            duration: 2,
            ease: "power2.out"
          });
        };

        document.addEventListener('mousemove', handleMouseMove);
      }
    });

    // Add CSS keyframes
    const style = document.createElement('style');
    style.textContent = `
      @keyframes float {
        0%, 100% { 
          transform: translateY(0px) rotateX(0deg) rotateY(0deg) rotateZ(0deg);
        }
        25% { 
          transform: translateY(-20px) rotateX(90deg) rotateY(45deg) rotateZ(15deg);
        }
        50% { 
          transform: translateY(-10px) rotateX(180deg) rotateY(90deg) rotateZ(30deg);
        }
        75% { 
          transform: translateY(-15px) rotateX(270deg) rotateY(135deg) rotateZ(45deg);
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return <FloatingContainer ref={containerRef} />;
};

export default Floating3DElements;